import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  TicketingPortalData,
  TicketingLandingAction,
  TicketingKpiItem,
  TicketingIncident,
  WidespreadBanner,
  DashboardCounts,
} from "../../../store/slices/ticketingPortalSlice";
import { RootState } from "../../../store/index";
import { formatUserName } from "../../../utils/emailTouserName";
import useTicketingAccess, { SUSTAIN_ONLY_ACTION_IDS } from "./useTicketingAccess";

export type LandingActionView = TicketingLandingAction & {
  badgeText?: string;
};

const REPORTING_ANALYTICS_ACTION: TicketingLandingAction = {
  id: "reporting-analytics",
  title: "Reporting and Analytics",
  description: "View ticketing analytics dashboards and trends.",
  metric: "",
  icon: "analytics",
  iconBackgroundColor: "#e8f4fe",
  iconColor: "#156fd5",
};

const TICKETING_CONFIGURATIONS_ACTION: TicketingLandingAction = {
  id: "ticketing-configurations",
  title: "Ticketing Configurations",
  description:
    "Configure ticket routing, teams, and the category taxonomy used on Submit Ticket.",
  metric: "Team Assignment",
  icon: "settings",
  iconBackgroundColor: "#ffe4d6",
  iconColor: "#e85d75",
};

export type TicketingLandingViewModel = {
  greeting: string;
  subtitle: string;
  incident: TicketingIncident | undefined;
  widespreadBanners: WidespreadBanner[];
  kpis: TicketingKpiItem[];
  kpisLoading: boolean;
  actions: LandingActionView[];
  showIncidentBanner: boolean;
  showKpis: boolean;
  isSustainTeamMember: boolean;
};

const useTicketingPortalLandingModel = (
  ticketingData: TicketingPortalData | null
) => {
  const authUser = useSelector((state: RootState) => state.auth.user);
  const widespreadBanners = useSelector((state: RootState) => state.ticketingPortal.widespreadBanners);
  const activeNotifCount = useSelector((state: RootState) => state.ticketingPortal.activeNotifCount);
  const teamMemberCount = useSelector((state: RootState) => state.ticketingPortal.teamMemberCount);
  const sustainTeamMembersLoading = useSelector((state: RootState) => state.ticketingPortal.sustainTeamMembersLoading);
  const sustainQueueLoading = useSelector((state: RootState) => state.ticketingPortal.sustainQueueLoading);
  const sustainQueueTotalElements = useSelector((state: RootState) => state.ticketingPortal.sustainQueueTotalElements);
  const sustainQueue = useSelector((state: RootState) => state.ticketingPortal.sustainQueue);
  const { isSustainTeamMember, canViewSustainFeatures, canViewReportingAnalytics, isReportingAnalyticsReadOnly } = useTicketingAccess();
  const myTicketsTotalElements = useSelector((state: RootState) => state.ticketingPortal.myTicketsTotalElements);
  const myTicketsLoading = useSelector((state: RootState) => state.ticketingPortal.myTicketsLoading);
  const dashboardCounts = useSelector((state: RootState) => state.ticketingPortal.dashboardCounts);
  const dashboardCountsLoading = useSelector((state: RootState) => state.ticketingPortal.dashboardCountsLoading);
  
  const userFirstName = useMemo(() => {
    if (!authUser) return "";
    const email = authUser.email || authUser.username || "";
    const full = formatUserName(email);
    // Return only the first name
    return full.split(" ")[0] || "";
  }, [authUser]);

  return useMemo<TicketingLandingViewModel>(() => {
    // Build live KPI items from API response; show skeleton values while loading or on error
    const liveKpis: TicketingKpiItem[] = dashboardCounts
      ? [
          { label: "Unassigned",        value: dashboardCounts.unassigned?.count    ?? 0, deltaText: dashboardCounts.unassigned?.metric    ?? "", tone: "positive" },
          { label: "Critical Open",     value: dashboardCounts.criticalOpen?.count  ?? 0, deltaText: dashboardCounts.criticalOpen?.metric  ?? "", tone: "critical" },
          { label: "Waiting on User",   value: dashboardCounts.waitingOnUser?.count ?? 0, deltaText: dashboardCounts.waitingOnUser?.metric ?? "", tone: "warning"  },
          { label: "Closed This Week",  value: dashboardCounts.closedThisWeek?.count?? 0, deltaText: dashboardCounts.closedThisWeek?.metric?? "", tone: "positive" },
        ]
      : [
          { label: "Unassigned",       value: 0, deltaText: "Unable to load", tone: "neutral" },
          { label: "Critical Open",    value: 0, deltaText: "Unable to load", tone: "neutral" },
          { label: "Waiting on User",  value: 0, deltaText: "Unable to load", tone: "neutral" },
          { label: "Closed This Week", value: 0, deltaText: "Unable to load", tone: "neutral" },
        ];

    // Use totalElements from the user-scoped /ticketing/tickets API (myTicketsTotalElements).
    // dashboardCounts.totalTicketCount is the system-wide count (all tickets) and must NOT
    // be used here — it would show the wrong number on the "My Tickets" action card.
    const myTicketsTotal = myTicketsTotalElements > 0 ? myTicketsTotalElements : (ticketingData?.myTickets?.total ?? 0);
    const myTicketsNeedsAction = ticketingData?.myTickets?.needsAction ?? 0;

    const baseActions = (
      ticketingData?.landing?.actions ?? [
        {
          id: "submit-ticket",
          title: "Submit Ticket",
          description:
            "Report an issue or request support for your site. PepGPT can help triage before you submit.",
          metric: "Avg 2 min",
          icon: "note-add",
          iconBackgroundColor: "#dce8fb",
          iconColor: "#156fd5",
        },
        {
          id: "my-tickets",
          title: "My Tickets",
          description:
            "Track status and progress of tickets you've submitted or are a stakeholder on.",
          metric: "1 needs action",
          icon: "confirmation-number",
          iconBackgroundColor: "#d9ecf8",
          iconColor: "#0f87c5",
        },
        {
          id: "sustain-queue",
          title: "Sustain Queue",
          description:
            "Triage incoming requests, assign ownership, and resolve across the portfolio.",
          metric: "28 active",
          icon: "grid-view",
          iconBackgroundColor: "#e4e0fd",
          iconColor: "#7a71e0",
          badgeText: "5 new",
        },
        {
          id: "notifications-comms",
          title: "Notifications & Comms",
          description:
            "Create targeted notifications, manage homepage banners, and measure delivery.",
          metric: "3 active",
          icon: "notifications-none",
          iconBackgroundColor: "#fbead8",
          iconColor: "#d0781d",
        },
        {
          id: "team-management",
          title: "Team Management",
          description:
            "Manage Sustain Team members, roles, and notification preferences.",
          metric: "8 members",
          icon: "groups",
          iconBackgroundColor: "#dff1de",
          iconColor: "#36a259",
        },
      ]
    );

    const hasReportingAnalyticsAction = baseActions.some((action) => action.id === REPORTING_ANALYTICS_ACTION.id);
    const actionsWithReporting = hasReportingAnalyticsAction
      ? baseActions
      : [...baseActions, REPORTING_ANALYTICS_ACTION];

    const hasConfigurationsAction = actionsWithReporting.some((action) => action.id === TICKETING_CONFIGURATIONS_ACTION.id);
    const actionsWithConfigurations = hasConfigurationsAction
      ? actionsWithReporting
      : [...actionsWithReporting, TICKETING_CONFIGURATIONS_ACTION];

    const actions: LandingActionView[] = actionsWithConfigurations.map((action) => {
      if (action.id === "my-tickets") {
        return {
          ...action,
          badgeText: myTicketsLoading ? undefined : String(myTicketsTotal),
          metric: myTicketsLoading ? action.metric : `${myTicketsTotal} ticket${myTicketsTotal === 1 ? "" : "s"}`,
          metricLoading: myTicketsLoading,
        };
      }
      if (action.id === "sustain-queue") {
        // Count active (non-closed) tickets from the fetched queue
        const activeCount = sustainQueue.filter((t) => t.status !== "resolved").length;
        const newCount = sustainQueue.filter((t) => t.status === "new").length;
        return {
          ...action,
          metric: sustainQueueLoading ? action.metric : `${activeCount} active`,
          metricLoading: sustainQueueLoading,
          badgeText: newCount > 0 ? `${newCount} new` : undefined,
        };
      }
      if (action.id === "notifications-comms") {
        return {
          ...action,
          metric: `${activeNotifCount} active`,
        };
      }
      if (action.id === "submit-ticket") {
        return {
          ...action,
          metric: "",
        };
      }
      if (action.id === "team-management") {
        return {
          ...action,
          metric: sustainTeamMembersLoading ? action.metric : `${teamMemberCount} members`,
          metricLoading: sustainTeamMembersLoading,
        };
      }
      if (action.id === "reporting-analytics") {
        return {
          ...action,
          metric: isReportingAnalyticsReadOnly ? "Read-only" : "",
        };
      }
      if (action.id === "ticketing-configurations") {
        return {
          ...action,
          metric: "Team Assignment",
        };
      }
      return action;
    }).filter((action) => {
      if (action.id === "reporting-analytics") {
        return canViewReportingAnalytics;
      }
      if (action.id === "ticketing-configurations") {
        return isSustainTeamMember;
      }

      return canViewSustainFeatures || !SUSTAIN_ONLY_ACTION_IDS.includes(action.id);
    });

    const sustainQueueAction = actions.find((a) => a.id === "sustain-queue");
    const sustainQueueCountMatch = sustainQueueAction?.badgeText?.match(/\d+/);
    const unassignedCount = sustainQueueCountMatch
      ? Number(sustainQueueCountMatch[0])
      : 5;

    const hour = new Date().getHours();
    const timeOfDay =
      hour >= 5 && hour < 12
        ? "morning"
        : hour >= 12 && hour < 17
        ? "afternoon"
        : hour >= 17 && hour < 21
        ? "evening"
        : "night";
    const greeting = `Good ${timeOfDay}, ${userFirstName || "there"}`;

    const subtitle = isSustainTeamMember
      ? `Triage incoming requests, manage your team, and keep the field informed. ${unassignedCount} ticket${unassignedCount === 1 ? "" : "s"} need assignment.`
      : "Submit requests, track their progress, and stay informed about warehouse-wide issues.";

    // Derive incident banner from widespread banners (first/most recent) or fall back to API data
    const activeWidespread = widespreadBanners[widespreadBanners.length - 1];
    const derivedIncident: TicketingIncident | undefined = activeWidespread
      ? {
          active: true,
          title: activeWidespread.title,
          message: activeWidespread.message,
          primaryActionLabel: "I'm Affected Too",
          secondaryActionLabel: "View",
        }
      : ticketingData?.landing?.incident;

    return {
      greeting,
      subtitle,
      incident: derivedIncident,
      widespreadBanners,
      kpis: liveKpis,
      kpisLoading: dashboardCountsLoading,
      actions,
      showIncidentBanner: widespreadBanners.length > 0 || (ticketingData?.landing?.incident?.active ?? false),
      showKpis: canViewSustainFeatures,
      isSustainTeamMember,
    };
  }, [ticketingData, userFirstName, widespreadBanners, activeNotifCount, teamMemberCount, isSustainTeamMember, canViewSustainFeatures, canViewReportingAnalytics, isReportingAnalyticsReadOnly, myTicketsTotalElements, myTicketsLoading, dashboardCounts, dashboardCountsLoading, sustainQueueLoading, sustainQueueTotalElements, sustainQueue]);
};

export default useTicketingPortalLandingModel;
